import React, { useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  InputNumber,
  Result,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  TruckOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import NumberWithUnit from '@/shared/components/NumberWithUnit'

const { Title, Text } = Typography

// ── Types ─────────────────────────────────────────────────────────────────────

interface ExpectedInward {
  do_id: string
  do_no: string
  do_date: string | null
  po_id: string
  po_no: string
  supplier_id: string | null
  supplier_name: string
  expected_date: string | null
  vehicle_no: string | null
  transporter_name: string | null
  driver_name: string | null
  driver_phone: string | null
  lr_no: string | null
  quantity_bales: number | null
  quantity_kg: string
  status: 'OPEN' | 'IN_TRANSIT'
  parakh_lot_count: number
  grn_created: boolean
}

interface ReceivedResult {
  grn_id: string
  grn_no: string
  gate_entry_id: string
  inward_no: string
  net_weight_kg: string
  bale_count: number
  do_no: string
  po_no: string
  supplier_name: string
  qc_assigned_at: string | null
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function isOverdue(d: string | null) {
  return !!d && dayjs(d).isBefore(dayjs(), 'day')
}

function fmtKg(v: string | number | null) {
  if (v === null || v === undefined) return '—'
  const n = Number(v)
  return n.toLocaleString('en-IN', { minimumFractionDigits: 3 })
}

// ── Receive Drawer ─────────────────────────────────────────────────────────────

interface ReceiveDrawerProps {
  do_: ExpectedInward | null
  open: boolean
  onClose: () => void
  plantId: string | undefined
}

function ReceiveDrawer({ do_, open: drawerOpen, onClose, plantId: plantId_ }: ReceiveDrawerProps) {
  const [form] = Form.useForm()
  const qc = useQueryClient()
  const [received, setReceived] = useState<ReceivedResult | null>(null)

  // Live net weight calculation
  const grossWt = Form.useWatch('gross_weight_kg', form)
  const tareWt = Form.useWatch('tare_weight_kg', form)
  const netWt =
    grossWt != null && tareWt != null && Number(grossWt) > 0 && Number(tareWt) > 0
      ? Number(grossWt) - Number(tareWt)
      : null

  // Variance vs DO expected
  const doQtyKg = do_ ? Number(do_.quantity_kg) : 0
  const variance = netWt !== null && doQtyKg > 0 ? netWt - doQtyKg : null
  const variancePct = variance !== null && doQtyKg > 0 ? (variance / doQtyKg) * 100 : null

  const receiveMut = useMutation({
    mutationFn: (payload: object) =>
      apiClient.post('/grn/receive-from-do', payload).then(r => r.data),
    onSuccess: (data: ReceivedResult) => {
      setReceived(data)
      qc.invalidateQueries({ queryKey: ['expected-inward'] })
      qc.invalidateQueries({ queryKey: ['grn-list'] })
      message.success(`GRN ${data.grn_no} created — QC assigned`)
    },
    onError: (e: any) =>
      message.error(e.response?.data?.detail || 'Failed to receive'),
  })

  const captureNow = (field: string) => {
    form.setFieldValue(field, dayjs().format('YYYY-MM-DDTHH:mm:ss'))
  }

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      receiveMut.mutate({
        do_id: do_!.do_id,
        plant_id: plantId_,
        rst_no: vals.rst_no,
        gross_weight_kg: vals.gross_weight_kg,
        gross_weighed_at: vals.gross_weighed_at,
        tare_weight_kg: vals.tare_weight_kg,
        tare_weighed_at: vals.tare_weighed_at,
        remarks: vals.remarks,
      })
    })
  }

  const handleClose = () => {
    setReceived(null)
    form.resetFields()
    onClose()
  }

  if (!do_) return null

  return (
    <Drawer
      title={
        <Space>
          <TruckOutlined style={{ color: '#1677ff' }} />
          <span>Receive — {do_.do_no}</span>
        </Space>
      }
      open={drawerOpen}
      onClose={handleClose}
      width={560}
      footer={
        !received ? (
          <Row justify="end">
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                size="large"
                loading={receiveMut.isPending}
                onClick={handleSubmit}
                icon={<CheckCircleOutlined />}
              >
                Receive &amp; Generate GRN
              </Button>
            </Space>
          </Row>
        ) : (
          <Row justify="end">
            <Button type="primary" onClick={handleClose}>
              Done
            </Button>
          </Row>
        )
      }
    >
      {received ? (
        /* ── Success Screen ─────────────────────────────── */
        <Result
          status="success"
          title="Vehicle Received Successfully"
          subTitle={`GRN ${received.grn_no} · Inward ${received.inward_no}`}
          extra={[
            <Descriptions key="d" bordered size="small" column={1}>
              <Descriptions.Item label="GRN No">
                <Text strong style={{ fontFamily: 'monospace' }}>{received.grn_no}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Inward No">
                <Text style={{ fontFamily: 'monospace' }}>{received.inward_no}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="DO / PO">
                {received.do_no} / {received.po_no}
              </Descriptions.Item>
              <Descriptions.Item label="Supplier">{received.supplier_name}</Descriptions.Item>
              <Descriptions.Item label="Net Weight">
                <Text strong>{fmtKg(received.net_weight_kg)} kg</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Bales">
                <Tag color="blue">{received.bale_count}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="QC">
                <Tag color="orange" icon={<ClockCircleOutlined />}>
                  QC Assigned
                </Tag>
              </Descriptions.Item>
            </Descriptions>,
          ]}
        />
      ) : (
        /* ── Receive Form ───────────────────────────────── */
        <Space direction="vertical" style={{ width: '100%' }} size="middle">

          {/* Auto-filled info — read only */}
          <Card
            size="small"
            style={{ background: '#f0f5ff', border: '1px solid #adc6ff' }}
            styles={{ body: { padding: '12px 16px' } }}
          >
            <Row gutter={0}>
              <Col span={24} style={{ marginBottom: 6 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>AUTO-FILLED FROM DELIVERY ORDER</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Supplier</Text>
                <div><Text strong>{do_.supplier_name || '—'}</Text></div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>DO / PO</Text>
                <div>
                  <Text strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{do_.do_no}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}> / {do_.po_no}</Text>
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row gutter={0}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Vehicle No</Text>
                <div><Text strong>{do_.vehicle_no || '—'}</Text></div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Driver</Text>
                <div>
                  <Text strong>{do_.driver_name || '—'}</Text>
                  {do_.driver_phone && (
                    <Text type="secondary" style={{ fontSize: 11 }}> · {do_.driver_phone}</Text>
                  )}
                </div>
              </Col>
            </Row>
            <Divider style={{ margin: '8px 0' }} />
            <Row gutter={0}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Expected Bales / Qty</Text>
                <div>
                  <Text strong>
                    {do_.quantity_bales ?? '—'} bales /{' '}
                    {fmtKg(do_.quantity_kg)} kg
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>LR No / Way Bill</Text>
                <div><Text>{do_.lr_no || '—'}</Text></div>
              </Col>
            </Row>
            {do_.parakh_lot_count > 0 && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Tag color="purple">
                  {do_.parakh_lot_count} QC-Passed Parakh lot(s) in this DO
                </Tag>
              </>
            )}
          </Card>

          {/* Security fills: RST + Weighbridge */}
          <Form form={form} layout="vertical" size="middle">
            {/* RST No */}
            <Form.Item
              name="rst_no"
              label={<Text strong>RST No (Weighbridge Slip)</Text>}
            >
              <Input
                placeholder="e.g. RST-2026-00123"
                size="large"
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>

            <Divider orientation="left" style={{ fontSize: 13 }}>
              Gross Weight (loaded truck)
            </Divider>

            <Row gutter={12} align="bottom">
              <Col span={14}>
                <Form.Item
                  name="gross_weight_kg"
                  label="Gross Weight (kg)"
                  rules={[{ required: true, message: 'Enter gross weight' }]}
                  style={{ marginBottom: 0 }}
                >
                  <NumberWithUnit unit="kg" size="large" style={{ width: '100%', fontFamily: 'monospace' }} precision={3} min={0} placeholder="e.g. 32450.000" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name="gross_weighed_at"
                  label="Time"
                  rules={[{ required: true, message: 'Capture time' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    readOnly
                    size="large"
                    style={{ fontFamily: 'monospace', fontSize: 12, background: '#f5f5f5' }}
                    placeholder="—"
                    suffix={
                      <Tooltip title="Capture current time">
                        <Button
                          type="link"
                          size="small"
                          icon={<FieldTimeOutlined />}
                          onClick={() => captureNow('gross_weighed_at')}
                          style={{ padding: 0, color: '#1677ff', fontWeight: 600 }}
                        >
                          Now
                        </Button>
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" style={{ fontSize: 13, marginTop: 20 }}>
              Tare Weight (empty truck)
            </Divider>

            <Row gutter={12} align="bottom">
              <Col span={14}>
                <Form.Item
                  name="tare_weight_kg"
                  label="Tare Weight (kg)"
                  rules={[{ required: true, message: 'Enter tare weight' }]}
                  style={{ marginBottom: 0 }}
                >
                  <NumberWithUnit unit="kg" size="large" style={{ width: '100%', fontFamily: 'monospace' }} precision={3} min={0} placeholder="e.g. 6030.000" />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name="tare_weighed_at"
                  label="Time"
                  rules={[{ required: true, message: 'Capture time' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    readOnly
                    size="large"
                    style={{ fontFamily: 'monospace', fontSize: 12, background: '#f5f5f5' }}
                    placeholder="—"
                    suffix={
                      <Tooltip title="Capture current time">
                        <Button
                          type="link"
                          size="small"
                          icon={<FieldTimeOutlined />}
                          onClick={() => captureNow('tare_weighed_at')}
                          style={{ padding: 0, color: '#1677ff', fontWeight: 600 }}
                        >
                          Now
                        </Button>
                      </Tooltip>
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Live Net Weight */}
            <Card
              size="small"
              style={{
                marginTop: 20,
                background: netWt !== null && netWt > 0 ? '#f6ffed' : '#fafafa',
                border: netWt !== null && netWt > 0 ? '1px solid #b7eb8f' : '1px solid #d9d9d9',
              }}
            >
              <Row gutter={16} align="middle">
                <Col span={12}>
                  <Statistic
                    title="Net Weight (Gross − Tare)"
                    value={netWt !== null && netWt > 0 ? fmtKg(netWt) : '—'}
                    suffix={netWt !== null ? 'kg' : ''}
                    valueStyle={{
                      fontSize: 22,
                      fontFamily: 'monospace',
                      color: netWt !== null && netWt > 0 ? '#389e0d' : '#aaa',
                      fontWeight: 700,
                    }}
                  />
                </Col>
                {variance !== null && (
                  <Col span={12}>
                    <Statistic
                      title="Variance vs DO"
                      value={`${variance >= 0 ? '+' : ''}${fmtKg(variance)} kg`}
                      suffix={variancePct !== null ? `(${variancePct >= 0 ? '+' : ''}${variancePct.toFixed(2)}%)` : ''}
                      valueStyle={{
                        fontSize: 14,
                        color:
                          Math.abs(variancePct ?? 0) <= 0.5
                            ? '#52c41a'
                            : Math.abs(variancePct ?? 0) <= 2
                            ? '#fa8c16'
                            : '#ff4d4f',
                      }}
                    />
                  </Col>
                )}
              </Row>
            </Card>

            <Form.Item name="remarks" label="Remarks" style={{ marginTop: 16 }}>
              <Input placeholder="Optional guard notes" />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Drawer>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function ExpectedInwardPage() {
  // Only stores roles (grn.create) may receive — gate/purchase
  // staff see the queue but not the Receive button (AUTH-001
  // follow-up: backend already enforces this with a 403).
  const canReceive = usePermission('grn.create')
  const { plantId } = usePlantId()
  const [viewDO, setViewDO] = useState<ExpectedInward | null>(null)
  const [receiveDO, setReceiveDO] = useState<ExpectedInward | null>(null)

  const { data: dos = [], isLoading } = useQuery<ExpectedInward[]>({
    queryKey: ['expected-inward', plantId],
    queryFn: () =>
      apiClient.get('/grn/expected-inward', { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
    refetchInterval: 60_000,
  })

  const openCount = dos.filter(d => d.status === 'OPEN').length
  const inTransitCount = dos.filter(d => d.status === 'IN_TRANSIT').length
  const overdueCount = dos.filter(d => isOverdue(d.expected_date)).length
  const grnDoneCount = dos.filter(d => d.grn_created).length

  const columns = [
    {
      title: 'DO / PO',
      key: 'do_no',
      render: (_: unknown, r: ExpectedInward) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontFamily: 'monospace' }}>{r.do_no}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>PO: {r.po_no}</Text>
        </Space>
      ),
    },
    {
      title: 'Supplier',
      key: 'supplier',
      render: (_: unknown, r: ExpectedInward) => (
        <Space direction="vertical" size={0}>
          <Text>{r.supplier_name || '—'}</Text>
          {r.transporter_name && (
            <Text type="secondary" style={{ fontSize: 11 }}>{r.transporter_name}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Vehicle / Driver',
      key: 'vehicle',
      render: (_: unknown, r: ExpectedInward) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.vehicle_no || '—'}</Text>
          {r.driver_name && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {r.driver_name}{r.driver_phone ? ` · ${r.driver_phone}` : ''}
            </Text>
          )}
          {r.lr_no && (
            <Text type="secondary" style={{ fontSize: 11 }}>LR: {r.lr_no}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Expected Date',
      dataIndex: 'expected_date',
      key: 'expected_date',
      render: (v: string | null) => {
        if (!v) return <Text type="secondary">—</Text>
        const od = isOverdue(v)
        return (
          <Text style={{ color: od ? '#ff4d4f' : undefined }}>
            {od && <ClockCircleOutlined style={{ marginRight: 4 }} />}
            {dayjs(v).format('DD MMM YYYY')}
          </Text>
        )
      },
    },
    {
      title: 'Expected Qty',
      key: 'qty',
      render: (_: unknown, r: ExpectedInward) => (
        <Space direction="vertical" size={0}>
          <Text>{r.quantity_bales ? `${r.quantity_bales} bales` : '—'}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {fmtKg(r.quantity_kg)} kg
          </Text>
          {r.parakh_lot_count > 0 && (
            <Tag color="purple" style={{ fontSize: 10 }}>
              {r.parakh_lot_count} Parakh lot{r.parakh_lot_count > 1 ? 's' : ''}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_: unknown, r: ExpectedInward) => (
        <Space direction="vertical" size={2}>
          <Tag color={r.status === 'IN_TRANSIT' ? 'blue' : 'orange'}>{r.status.replace('_', ' ')}</Tag>
          {r.grn_created && (
            <Tag color="green" icon={<CheckCircleOutlined />} style={{ fontSize: 10 }}>
              GRN Done
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 130,
      render: (_: unknown, r: ExpectedInward) => (
        <Space>
          <Tooltip title="View details">
            <Button size="small" icon={<EyeOutlined />} onClick={() => setViewDO(r)} />
          </Tooltip>
          {!r.grn_created && canReceive && (
            <Button
              size="small"
              type="primary"
              icon={<TruckOutlined />}
              onClick={() => setReceiveDO(r)}
            >
              Receive
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Expected Inward
          </Title>
          <Text type="secondary">OPEN &amp; IN-TRANSIT Delivery Orders — click Receive when vehicle arrives</Text>
        </Col>
      </Row>

      {/* KPI Cards */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        {[
          { label: 'Open (Not Dispatched)', val: openCount, color: '#fa8c16' },
          { label: 'In Transit', val: inTransitCount, color: '#1677ff' },
          { label: 'Overdue', val: overdueCount, color: overdueCount > 0 ? '#ff4d4f' : '#52c41a' },
          { label: 'GRN Created', val: grnDoneCount, color: '#52c41a' },
        ].map(k => (
          <Col xs={12} sm={6} key={k.label}>
            <Card size="small">
              <Statistic title={k.label} value={k.val} valueStyle={{ color: k.color }} />
            </Card>
          </Col>
        ))}
      </Row>

      {overdueCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${overdueCount} shipment${overdueCount > 1 ? 's are' : ' is'} overdue.`}
          style={{ marginBottom: 12 }}
        />
      )}

      <Card>
        <Table
          dataSource={dos}
          columns={columns}
          rowKey="do_id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 20 }}
          rowClassName={(r) =>
            r.grn_created ? 'row-done' : isOverdue(r.expected_date) ? 'row-overdue' : ''
          }
        />
      </Card>

      {/* View Details Drawer */}
      <Drawer
        title={`DO Details — ${viewDO?.do_no}`}
        open={!!viewDO}
        onClose={() => setViewDO(null)}
        width={480}
        extra={
          viewDO && !viewDO.grn_created && canReceive && (
            <Button
              type="primary"
              icon={<TruckOutlined />}
              onClick={() => { setViewDO(null); setReceiveDO(viewDO) }}
            >
              Receive
            </Button>
          )
        }
      >
        {viewDO && (
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="DO No">{viewDO.do_no}</Descriptions.Item>
            <Descriptions.Item label="PO No">{viewDO.po_no}</Descriptions.Item>
            <Descriptions.Item label="Supplier">{viewDO.supplier_name}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={viewDO.status === 'IN_TRANSIT' ? 'blue' : 'orange'}>
                {viewDO.status}
              </Tag>
              {viewDO.grn_created && <Tag color="green">GRN Done</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Expected Date">
              {viewDO.expected_date ? dayjs(viewDO.expected_date).format('DD MMM YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Expected Qty">
              {viewDO.quantity_bales ?? '—'} bales / {fmtKg(viewDO.quantity_kg)} kg
            </Descriptions.Item>
            <Descriptions.Item label="Vehicle">{viewDO.vehicle_no || '—'}</Descriptions.Item>
            <Descriptions.Item label="Transporter">{viewDO.transporter_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Driver">
              {viewDO.driver_name || '—'}
              {viewDO.driver_phone ? ` · ${viewDO.driver_phone}` : ''}
            </Descriptions.Item>
            <Descriptions.Item label="LR No">{viewDO.lr_no || '—'}</Descriptions.Item>
            {viewDO.parakh_lot_count > 0 && (
              <Descriptions.Item label="Parakh Lots">
                <Tag color="purple">{viewDO.parakh_lot_count} QC-Passed lot(s)</Tag>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Drawer>

      {/* Receive Drawer */}
      <ReceiveDrawer
        do_={receiveDO}
        open={!!receiveDO}
        onClose={() => setReceiveDO(null)}
        plantId={plantId}
      />

      <style>{`
        .row-overdue td { background: #fff2f0 !important; }
        .row-done td { background: #f6ffed !important; opacity: 0.75; }
      `}</style>
    </div>
  )
}
